/**
 * B.7.e Step 4 — kick off the provisioner + poll the
 * `portalProvisioningJobs` row until status='complete' or 'failed'.
 *
 * Flow:
 *   1. Operator clicks "Start provisioning".
 *   2. Acquire an MSAL Graph token (popup if needed; surfaces a
 *      MicrosoftSignInButton when no MS account is linked).
 *   3. Call `portalProvisioning.cloneTemplate` → creates the
 *      `portalProvisioningJobs` row.
 *   4. Bundle each upload's sanitized body into a per-matter map.
 *   5. Call `portalProvisioning.kickProvisioner` with the job id +
 *      Graph token + tenant ID + uploadedByMatter map.
 *   6. The Function App takes over and drives Graph; this UI just
 *      subscribes to the job row via `useQuery(getJob)` and renders the
 *      progress bar.
 */

import { useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { Id } from '../../../convex/_generated/dataModel';
import {
  acquireGraphToken,
  connectMicrosoftAccount,
  getActiveAccount,
  getMsalAccountTenantId,
} from '../../lib/graph/msalClient';
import type { WizardState } from './WizardShell';
import type { MatterDocFolder, UploadedFile } from './Step2Upload';
import type { SanitizeOutcome } from './Step3Sanitize';

interface Step4Props {
  state: WizardState;
  onBack: () => void;
  onJobCreated: (jobId: Id<'portalProvisioningJobs'>) => void;
}

interface UploadedDoc {
  name: string;
  body: string;
  folder: MatterDocFolder;
}

/**
 * Build `uploadedByMatter` for the kickProvisioner action.
 * The body sent to the Function App is the post-sanitize text when the
 * operator confirmed sanitization in Step 3; otherwise the raw upload
 * body (Step 3 leaves outcomes 'pending' or 'failed' if the operator
 * skipped them — we still forward, since the wizard's privacy gate
 * already vouched for them).
 */
function buildUploadedByMatter(
  uploads: ReadonlyArray<UploadedFile>,
  outcomes: Readonly<Record<string, SanitizeOutcome>>,
): Record<string, UploadedDoc[]> {
  const byMatter: Record<string, UploadedDoc[]> = {};
  for (const u of uploads) {
    const outcome = outcomes[u.name];
    const body =
      outcome !== undefined &&
      outcome.status === 'ok' &&
      outcome.sanitizedBody !== undefined
        ? outcome.sanitizedBody
        : u.body;
    const doc: UploadedDoc = { name: u.name, body, folder: u.folder };
    const list = byMatter[u.matterCode] ?? [];
    list.push(doc);
    byMatter[u.matterCode] = list;
  }
  return byMatter;
}

export function Step4Provision({ state, onBack, onJobCreated }: Step4Props) {
  const cloneTemplate = useMutation(api.portalProvisioning.cloneTemplate);
  const kickProvisioner = useAction(api.portalProvisioning.kickProvisioner);
  const job = useQuery(
    api.portalProvisioning.getJob,
    state.jobId !== null ? { jobId: state.jobId } : 'skip',
  );
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [needsMsConnect, setNeedsMsConnect] = useState(false);

  async function ensureMsAccount(): Promise<{ token: string; tenantId: string }> {
    if (getActiveAccount() === null) {
      await connectMicrosoftAccount();
    }
    const token = await acquireGraphToken();
    const tenantId = getMsalAccountTenantId();
    if (tenantId === null) {
      throw new Error(
        'Microsoft sign-in returned no tenant ID. Sign out and back in.',
      );
    }
    return { token, tenantId };
  }

  async function startProvision() {
    if (state.templateId === null) return;
    setStarting(true);
    setStartError(null);
    setNeedsMsConnect(false);
    try {
      // 1. Microsoft sign-in + Graph token.
      const { token: graphAccessToken, tenantId: operatorTenantId } =
        await ensureMsAccount();

      // 2. Clone the template → creates the job row.
      const cloneResult = await cloneTemplate({
        templateId: state.templateId,
        targetClientSlug: state.targetClientSlug,
      });
      if (!cloneResult.ok) return;
      const jobId = cloneResult.jobId;
      onJobCreated(jobId);

      // 3. Bundle the operator's uploads by matter code.
      const uploadedByMatter = buildUploadedByMatter(
        state.uploadedFiles,
        state.sanitizeOutcomes,
      );

      // 4. Kick the Function App.
      const kickResult = await kickProvisioner({
        jobId,
        graphAccessToken,
        operatorTenantId,
        uploadedByMatter,
      });
      if (!kickResult.ok) {
        setStartError(kickResult.reason);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (
        message.includes('No Microsoft account connected') ||
        message.includes('user_cancelled') ||
        message.includes('AADSTS')
      ) {
        setNeedsMsConnect(true);
      }
      setStartError(message);
    } finally {
      setStarting(false);
    }
  }

  if (state.jobId === null) {
    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Provision portal</h2>
          <p className="text-sm text-gray-800 mb-3">
            Ready to clone the template into{' '}
            <code className="font-mono">/c/{state.targetClientSlug}/desktop</code>
            . The Function App provisioner will create the SharePoint site,
            seed the matter folders, and upload the sanitized docs.
          </p>
          <p className="text-xs text-gray-700">
            Signing in to Microsoft uses your operator account — the wizard
            captures the Azure AD tenant you authenticate against and pins
            future visitors to the new portal to that same tenant.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid={testIds.portalWizard.step4Back}
            onClick={onBack}
            className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100"
          >
            Back
          </button>
          <button
            type="button"
            data-testid={testIds.portalWizard.provisionStart}
            onClick={() => void startProvision()}
            disabled={starting}
            className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
          >
            {starting ? 'Starting…' : 'Start provisioning'}
          </button>
          {needsMsConnect && (
            <button
              type="button"
              onClick={() => {
                void connectMicrosoftAccount().then(() => {
                  setNeedsMsConnect(false);
                  setStartError(null);
                });
              }}
              className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100"
            >
              Sign in to Microsoft
            </button>
          )}
        </div>
        {startError !== null && (
          <p
            data-testid={testIds.portalWizard.provisionError}
            className="text-sm text-red-700"
          >
            {startError}
          </p>
        )}
      </section>
    );
  }

  // Job created; poll its status.
  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Provisioning&hellip;</h2>
        <p className="text-sm text-gray-800 mb-3">
          Job id <code className="font-mono">{state.jobId}</code>. Convex
          reactivity keeps this view in sync with the Function App.
        </p>
      </div>

      {job === undefined && (
        <p className="text-sm text-gray-800">Loading job&hellip;</p>
      )}
      {job !== undefined && job !== null && (
        <div
          data-testid={testIds.portalWizard.provisionProgress}
          data-job-status={job.status}
          className="border border-black/30 bg-white p-4 space-y-2"
        >
          <div className="text-sm">
            <span className="font-semibold">Status:</span> {job.status}
          </div>
          <div className="text-sm">
            <span className="font-semibold">Step:</span>{' '}
            {job.stepsCompleted}/{job.stepsTotal} — {job.lastStepLabel}
          </div>
          <div className="w-full bg-gray-200 h-2 border border-black/40">
            <div
              className="bg-black h-full"
              style={{
                width: `${Math.round(
                  (job.stepsCompleted / job.stepsTotal) * 100,
                )}%`,
              }}
            />
          </div>
          {job.status === 'complete' && (
            <a
              href={`/c/${state.targetClientSlug}/desktop`}
              data-testid={testIds.portalWizard.provisionResultUrl}
              className="block underline text-sm pt-2"
            >
              Open the new portal at /c/{state.targetClientSlug}/desktop
            </a>
          )}
          {job.status === 'failed' && (
            <p
              data-testid={testIds.portalWizard.provisionError}
              className="text-sm text-red-700 pt-2"
            >
              {job.errorMessage ?? 'Unknown error'}
            </p>
          )}
        </div>
      )}

      {startError !== null && (
        <p
          data-testid={testIds.portalWizard.provisionError}
          className="text-sm text-red-700"
        >
          {startError}
        </p>
      )}
    </section>
  );
}
